import Meta from '../../components/Meta'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Discount_Type_Enum_Enum,
  GetDiscountTrainingsDocument,
  GetDiscountTrainingsQuery,
  GetDiscountTrainingsQueryVariables,
  useDeleteDiscountTrainingMutation,
} from '../../generated/urql.administrator'
import { useClient } from 'urql'
import { Button, ButtonGroup } from 'react-bootstrap'
import { PencilSquare, Plus, Trash } from 'react-bootstrap-icons'
import { InfiniteTable } from '../../components/InfiniteTable'
import DiscountTrainingModal, {
  DiscountTrainingModalType,
} from '../../components/DiscountTrainingModal'

const BatchSize = 20

const DiscountTrainings: FC<{
  id: number
  readOnly?: boolean
  discountType: Discount_Type_Enum_Enum
}> = ({ id, readOnly, discountType }) => {
  // page content
  const pageTitle = 'Szkolenia'
  const [discountTrainings, setDiscountTrainings] = useState<
    GetDiscountTrainingsQuery['discount_training']
  >([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const data = useMemo(() => discountTrainings, [discountTrainings])
  const client = useClient()
  const discountTrainingModalRef = useRef<DiscountTrainingModalType>(null)
  const [, deleteDiscountTraining] = useDeleteDiscountTrainingMutation()

  const loadMoreRows = useCallback(
    async ({ refetch = false }: { refetch: boolean } = { refetch: false }) => {
      const { data, error } = await client
        .query<GetDiscountTrainingsQuery, GetDiscountTrainingsQueryVariables>(
          GetDiscountTrainingsDocument,
          { discountId: id, offset: refetch ? 0 : offset, limit: BatchSize },
          { requestPolicy: refetch ? 'network-only' : undefined }
        )
        .toPromise()

      if (error) {
        console.error(error)
        return
      }

      if (data) {
        setDiscountTrainings(
          refetch
            ? data.discount_training
            : discountTrainings.concat(data.discount_training)
        )
        setOffset(refetch ? BatchSize : offset + BatchSize)
        setCount(data.discount_training_aggregate.aggregate?.count || 0)
      }
    },
    [client, discountTrainings, offset]
  )

  useEffect(() => {
    loadMoreRows({ refetch: true }).then(() => {})
  }, [])

  return (
    <div>
      <Meta title={pageTitle} />
      <div className="mt-3 mb-3 d-flex justify-content-end">
        <ButtonGroup>
          <Button
            className="d-flex align-items-center"
            disabled={readOnly}
            onClick={() =>
              discountTrainingModalRef.current?.show({ discountId: id })
            }
          >
            <Plus size={20} />
            &nbsp;Dodaj
          </Button>
        </ButtonGroup>
      </div>
      <InfiniteTable<GetDiscountTrainingsQuery['discount_training'][number]>
        data={data}
        columns={[
          { Header: 'ID', accessor: 'trainingId' },
          {
            Header: 'Szkolenie',
            accessor: ({ training }) => training.name,
          },
          {
            Header: 'Maksymalna liczba wystąpień',
            accessor: ({ maxCount }) => (
              <span className="text-nowrap">
                {Number.isInteger(maxCount) ? maxCount : 'nieograniczona'}
              </span>
            ),
            width:
              discountType === Discount_Type_Enum_Enum.WholeOrderWithItems
                ? 200
                : 0,
          },
          {
            Header: '',
            id: 'actions',
            accessor: (rowData) => (
              <div className="d-flex justify-content-end">
                <ButtonGroup>
                  <Button
                    variant="primary"
                    disabled={readOnly}
                    onClick={() =>
                      discountTrainingModalRef.current?.show({
                        discountId: id,
                        trainingId: rowData.trainingId,
                      })
                    }
                  >
                    <PencilSquare size={18} />
                  </Button>
                  <Button
                    variant="danger"
                    disabled={readOnly}
                    onClick={async () => {
                      if (
                        window.confirm('Czy napewno chcesz usunąć szkolenie?')
                      ) {
                        await deleteDiscountTraining({
                          discountId: id,
                          trainingId: rowData.trainingId,
                        })
                        await loadMoreRows({ refetch: true })
                      }
                    }}
                  >
                    <Trash size={18} />
                  </Button>
                </ButtonGroup>
              </div>
            ),
          },
        ]}
        loadMoreRows={loadMoreRows}
        hasMore={offset < count}
      />
      <DiscountTrainingModal
        ref={discountTrainingModalRef}
        onChange={() => loadMoreRows({ refetch: true })}
        discountType={discountType}
      />
    </div>
  )
}

export default DiscountTrainings
